import { useCallback, useState } from 'react';
import { authApi } from '../api/auth';

export interface UseLogoutOptions {
  /** Injectable for tests — defaults to a hard navigation to `/login`. */
  navigate?: (path: string) => void;
}

export interface UseLogoutResult {
  /** Calls `POST /api/auth/logout`, then ALWAYS hard-navigates to `/login`. */
  logout: () => Promise<void>;
  /** True from the moment `logout()` is called until the redirect fires. */
  isLoggingOut: boolean;
}

function defaultNavigate(path: string): void {
  window.location.href = path;
}

/**
 * Drives the main-app sidebar logout control.
 *
 * `POST /api/auth/logout` deletes the server-side session and clears the
 * `g2_session` cookie. The redirect to `/login` happens unconditionally in a
 * `finally` — even a failed/network-errored request still lands the user on
 * the login screen (fail-safe logout: the user's intent is to leave, and a
 * stale cookie is harmless once no page depending on it is mounted).
 *
 * The navigation is a HARD one (`window.location.href`, never a client-side
 * router transition), mirroring `lib/orgCache.ts`'s `discardOrgScopedCacheAndNavigate`
 * — it is the only way to guarantee every mounted component and
 * module-level variable is discarded along with the session, not just the
 * ones that happen to read session state today.
 */
export function useLogout(options: UseLogoutOptions = {}): UseLogoutResult {
  const { navigate = defaultNavigate } = options;
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await authApi.logout();
    } catch (err) {
      // Fail-safe: proceed to the redirect below even if the request
      // failed — never strand the user in a half-authenticated UI.
      // Preserve client-side telemetry for the network failure at no
      // behavioural cost (the backend logs its own server-side failures).
      console.warn('Logout request failed; clearing session client-side anyway', err);
    } finally {
      navigate('/login');
    }
  }, [navigate]);

  return { logout, isLoggingOut };
}
